/**
 * Builds the remote DevExtreme data sources and page-lifetime detail cache for
 * Archived Emails. Only fixed fields and operators are translated; arbitrary
 * DevExtreme expressions never cross the API boundary.
 */
import CustomStore from "devextreme/data/custom_store";
import archivedEmailsApi, {
	ARCHIVED_EMAIL_ERROR_CODES,
	ARCHIVED_EMAIL_ERROR_MESSAGES,
	isArchivedEmailAuthorizationError,
	isArchivedEmailCancellationError,
} from "../../api/email/archivedEmailsApi";

export const ARCHIVED_EMAIL_DEFAULT_PAGE_SIZE = 25;
export const ARCHIVED_EMAIL_MAXIMUM_PAGE_SIZE = 100;
export const ARCHIVED_EMAIL_MAXIMUM_HEADER_SELECTIONS = 20;
export const UNSUPPORTED_ARCHIVED_EMAIL_FILTER_CODE =
	"ARCHIVED_EMAIL_UNSUPPORTED_FILTER";
export const UNSUPPORTED_ARCHIVED_EMAIL_FILTER_MESSAGE =
	"This archived-email filter is not supported.";

const TEXT_FIELD_RULES = Object.freeze({
	caseNumber: {
		filterKey: "caseNumber",
		exactKey: "caseNumberValues",
		maximumLength: 13,
		uppercase: true,
	},
	sender: {
		filterKey: "sender",
		exactKey: "senderValues",
		maximumLength: 320,
		uppercase: false,
	},
	recipient: {
		filterKey: "recipient",
		exactKey: "recipientValues",
		maximumLength: 320,
		uppercase: false,
	},
	subject: {
		filterKey: "subject",
		exactKey: "subjectValues",
		maximumLength: 512,
		uppercase: false,
	},
});

const SORT_FIELDS = new Set([
	"caseNumber",
	"sender",
	"recipient",
	"subject",
	"sentDateTime",
]);

/**
 * Produces the one fixed validation error used for unsupported filters.
 *
 * @returns {Error} Safe local filter error.
 */
const unsupportedFilter = () => {
	const error = new Error(UNSUPPORTED_ARCHIVED_EMAIL_FILTER_MESSAGE);
	error.name = "ArchivedEmailFilterError";
	error.code = UNSUPPORTED_ARCHIVED_EMAIL_FILTER_CODE;
	error.isArchivedEmailSafeError = true;
	return error;
};

/**
 * Produces a UI-safe operational error with a stable category.
 *
 * @param {string} code Stable error category.
 * @param {string} message Fixed user-facing message.
 * @returns {Error} Sanitized error.
 */
const createUiError = (code, message) => {
	const error = new Error(message);
	error.name = "ArchivedEmailError";
	error.code = code;
	error.isArchivedEmailSafeError = true;
	return error;
};

/**
 * Normalizes a non-negative DevExtreme offset.
 *
 * @param {*} value Untrusted load option.
 * @returns {number} Safe row offset.
 */
const normalizeSkip = (value) =>
	Number.isInteger(value) && value >= 0 ? value : 0;

/**
 * Normalizes a positive DevExtreme page size and applies the API maximum.
 *
 * @param {*} value Untrusted load option.
 * @returns {number} Safe page size.
 */
const normalizeTake = (value) =>
	Number.isInteger(value) && value > 0
		? Math.min(value, ARCHIVED_EMAIL_MAXIMUM_PAGE_SIZE)
		: ARCHIVED_EMAIL_DEFAULT_PAGE_SIZE;

/**
 * Normalizes and bounds a supported text-filter value.
 *
 * @param {*} value Untrusted DevExtreme filter value.
 * @param {object} rule Field-specific length and casing rule.
 * @returns {string} Normalized value, or an empty string when omitted.
 * @throws {Error} Controlled error for invalid or overlong values.
 */
const normalizeTextValue = (value, rule) => {
	if (value === undefined || value === null) return "";
	if (typeof value !== "string") throw unsupportedFilter();

	let normalized = value.trim();
	if (!normalized) return "";
	if (normalized.length > rule.maximumLength) {
		throw unsupportedFilter();
	}
	if (rule.uppercase) normalized = normalized.toUpperCase();
	return normalized;
};

/**
 * Parses a DevExtreme date value, including date-only strings as local dates.
 * Treating date-only input as local preserves the calendar day selected by the
 * user before the boundary is converted to an explicit UTC timestamp.
 *
 * @param {*} value Date, ISO string, or timestamp.
 * @returns {Date} Valid copied date.
 * @throws {Error} Controlled error for an invalid date.
 */
const parseDateValue = (value) => {
	let date;
	if (
		typeof value === "string" &&
		/^\d{4}-\d{2}-\d{2}$/.test(value)
	) {
		const [year, month, day] = value.split("-").map(Number);
		date = new Date(year, month - 1, day);
	} else {
		date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
	}

	if (Number.isNaN(date.getTime())) throw unsupportedFilter();
	return date;
};

/**
 * Returns the UTC representation of a local calendar-day boundary.
 *
 * @param {Date} value Selected local date.
 * @param {number} additionalDays Days to add to the local calendar date.
 * @returns {string} ISO-8601 UTC timestamp.
 */
const localDayBoundaryToUtc = (value, additionalDays = 0) =>
	new Date(
		value.getFullYear(),
		value.getMonth(),
		value.getDate() + additionalDays
	).toISOString();

/**
 * Merges one lower Date Sent bound, retaining the most restrictive value.
 *
 * @param {object} state Accumulated filter state.
 * @param {string} value ISO-8601 lower bound.
 */
const addLowerDateBound = (state, value) => {
	if (!state.sentFromUtc || value > state.sentFromUtc) {
		state.sentFromUtc = value;
	}
};

/**
 * Merges one exclusive upper Date Sent bound, retaining the most restrictive value.
 *
 * @param {object} state Accumulated filter state.
 * @param {string} value ISO-8601 exclusive upper bound.
 */
const addUpperDateBound = (state, value) => {
	if (!state.sentToUtc || value < state.sentToUtc) {
		state.sentToUtc = value;
	}
};

/**
 * Adds normalized exact header-filter values to one field.
 *
 * @param {object} state Accumulated filter state.
 * @param {string} field Approved text field.
 * @param {Array} values Untrusted exact values.
 */
const addExactValues = (state, field, values) => {
	const rule = TEXT_FIELD_RULES[field];
	if (!rule || !Array.isArray(values)) throw unsupportedFilter();

	for (const value of values) {
		const normalized = normalizeTextValue(value, rule);
		if (!normalized) continue;

		const duplicate = state.exact[field].some(
			(candidate) =>
				candidate.localeCompare(normalized, undefined, {
					sensitivity: "accent",
				}) === 0
		);
		if (!duplicate) state.exact[field].push(normalized);
		if (
			state.exact[field].length >
			ARCHIVED_EMAIL_MAXIMUM_HEADER_SELECTIONS
		) {
			throw unsupportedFilter();
		}
	}
};

/**
 * Applies one approved leaf filter to the accumulated query state.
 *
 * @param {Array} expression DevExtreme leaf expression.
 * @param {object} state Accumulated filter state.
 */
const applyLeafFilter = (expression, state) => {
	if (
		expression.length !== 3 ||
		typeof expression[0] !== "string" ||
		typeof expression[1] !== "string"
	) {
		throw unsupportedFilter();
	}

	const [field, rawOperator, value] = expression;
	const operator = rawOperator.toLowerCase();
	const textRule = TEXT_FIELD_RULES[field];

	if (textRule) {
		if (operator === "contains") {
			const normalized = normalizeTextValue(value, textRule);
			if (!normalized) return;
			if (
				state.text[field] &&
				state.text[field] !== normalized
			) {
				throw unsupportedFilter();
			}
			state.text[field] = normalized;
			return;
		}

		if (operator === "=") {
			addExactValues(state, field, [value]);
			return;
		}

		if (operator === "anyof") {
			addExactValues(state, field, value);
			return;
		}

		throw unsupportedFilter();
	}

	if (field !== "sentDateTime") throw unsupportedFilter();

	if (operator === "=") {
		const date = parseDateValue(value);
		addLowerDateBound(state, localDayBoundaryToUtc(date));
		addUpperDateBound(state, localDayBoundaryToUtc(date, 1));
		return;
	}

	if (operator === ">=") {
		addLowerDateBound(state, parseDateValue(value).toISOString());
		return;
	}

	if (operator === "<") {
		addUpperDateBound(state, parseDateValue(value).toISOString());
		return;
	}

	throw unsupportedFilter();
};

/**
 * Collects a DevExtreme OR expression only when every branch is an approved
 * equality selection for the same text field. This supports the alternate
 * expression form emitted by DevExtreme header filters.
 *
 * @param {Array} expression OR expression.
 * @returns {{field: string, values: Array}} Homogeneous exact selections.
 */
const collectHeaderOrValues = (expression) => {
	if (
		expression.length === 3 &&
		typeof expression[0] === "string"
	) {
		const [field, rawOperator, value] = expression;
		const operator =
			typeof rawOperator === "string"
				? rawOperator.toLowerCase()
				: "";
		if (!TEXT_FIELD_RULES[field]) throw unsupportedFilter();
		if (operator === "=") return { field, values: [value] };
		if (operator === "anyof" && Array.isArray(value)) {
			return { field, values: value };
		}
		throw unsupportedFilter();
	}

	if (!Array.isArray(expression) || expression.length < 3) {
		throw unsupportedFilter();
	}

	const operators = expression.filter((_, index) => index % 2 === 1);
	if (operators.some((operator) => String(operator).toLowerCase() !== "or")) {
		throw unsupportedFilter();
	}

	const branches = expression.filter((_, index) => index % 2 === 0);
	const collected = branches.map(collectHeaderOrValues);
	const field = collected[0]?.field;
	if (!field || collected.some((item) => item.field !== field)) {
		throw unsupportedFilter();
	}

	return {
		field,
		values: collected.flatMap((item) => item.values),
	};
};

/**
 * Applies a supported DevExtreme filter tree. AND may join approved filters;
 * OR is restricted to same-field header selections.
 *
 * @param {*} expression DevExtreme filter expression.
 * @param {object} state Accumulated filter state.
 */
const applyFilterExpression = (expression, state) => {
	if (!expression) return;
	if (!Array.isArray(expression)) throw unsupportedFilter();

	if (
		expression.length === 3 &&
		typeof expression[0] === "string"
	) {
		applyLeafFilter(expression, state);
		return;
	}

	if (expression.length < 3 || expression.length % 2 === 0) {
		throw unsupportedFilter();
	}

	const operators = expression
		.filter((_, index) => index % 2 === 1)
		.map((operator) => String(operator).toLowerCase());
	if (operators.every((operator) => operator === "and")) {
		expression
			.filter((_, index) => index % 2 === 0)
			.forEach((branch) => applyFilterExpression(branch, state));
		return;
	}

	if (operators.every((operator) => operator === "or")) {
		const exact = collectHeaderOrValues(expression);
		addExactValues(state, exact.field, exact.values);
		return;
	}

	throw unsupportedFilter();
};

/**
 * Creates an empty typed filter accumulator.
 *
 * @returns {object} Mutable state used only during one translation.
 */
const createFilterState = () => ({
	text: {},
	exact: Object.fromEntries(
		Object.keys(TEXT_FIELD_RULES).map((field) => [field, []])
	),
	sentFromUtc: null,
	sentToUtc: null,
});

/**
 * Applies one allowlisted single-column sort or the contract's default.
 *
 * @param {*} sort DevExtreme sort option.
 * @param {object} query API query being built.
 */
const applySort = (sort, query) => {
	if (!sort || (Array.isArray(sort) && sort.length === 0)) return;

	const descriptors = Array.isArray(sort) ? sort : [sort];
	if (descriptors.length > 2) throw unsupportedFilter();

	const descriptor = descriptors[0];
	const selector =
		typeof descriptor === "string" ? descriptor : descriptor?.selector;
	if (typeof selector !== "string" || !SORT_FIELDS.has(selector)) {
		throw unsupportedFilter();
	}

	// DevExtreme appends the grid key as a second sort descriptor to stabilize
	// remote pages. EmailAPI already applies the authoritative ID-descending
	// tie-break, so accept only that generated descriptor and do not forward it.
	const tieBreakDescriptor = descriptors[1];
	const tieBreakSelector =
		typeof tieBreakDescriptor === "string"
			? tieBreakDescriptor
			: tieBreakDescriptor?.selector;
	if (tieBreakDescriptor && tieBreakSelector !== "id") {
		throw unsupportedFilter();
	}

	query.sortField = selector;
	query.sortDescending =
		typeof descriptor === "object" && descriptor?.desc === true;
};

/**
 * Maps DevExtreme grid load options into the fixed Task 01 API query.
 *
 * @param {object} loadOptions DevExtreme remote load options.
 * @returns {object} Bounded, allowlisted LawbaseAPI query.
 * @throws {Error} Controlled error for an unsupported expression.
 */
export const mapArchivedEmailLoadOptionsToQuery = (
	loadOptions = {}
) => {
	const query = {
		skip: normalizeSkip(loadOptions.skip),
		take: normalizeTake(loadOptions.take),
		sortField: "sentDateTime",
		sortDescending: true,
	};

	applySort(loadOptions.sort, query);

	const state = createFilterState();
	applyFilterExpression(loadOptions.filter, state);

	Object.entries(TEXT_FIELD_RULES).forEach(([field, rule]) => {
		if (state.text[field]) query[rule.filterKey] = state.text[field];
		if (state.exact[field].length > 0) {
			query[rule.exactKey] = state.exact[field];
		}
	});
	if (state.sentFromUtc) query.sentFromUtc = state.sentFromUtc;
	if (state.sentToUtc) query.sentToUtc = state.sentToUtc;
	if (
		query.sentFromUtc &&
		query.sentToUtc &&
		query.sentFromUtc >= query.sentToUtc
	) {
		throw unsupportedFilter();
	}

	return query;
};

/**
 * Maps a header-filter load into the bounded distinct-value contract.
 *
 * @param {string} field Approved text field.
 * @param {object} loadOptions DevExtreme header-filter load options.
 * @returns {object} Bounded filter-values query.
 */
export const mapArchivedEmailHeaderFilterLoadOptionsToQuery = (
	field,
	loadOptions = {}
) => {
	if (!TEXT_FIELD_RULES[field]) throw unsupportedFilter();

	const search =
		typeof loadOptions.searchValue === "string"
			? loadOptions.searchValue.trim().slice(0, 100)
			: "";

	return {
		field,
		skip: normalizeSkip(loadOptions.skip),
		take: normalizeTake(loadOptions.take),
		...(search ? { search } : {}),
	};
};

/**
 * Rethrows authorization and cancellation errors, replacing all other list
 * failures with the fixed UI-safe message.
 *
 * @param {*} error Underlying API error.
 * @throws {Error} Preserved control-flow error or sanitized list error.
 */
const throwListError = (error) => {
	if (
		isArchivedEmailAuthorizationError(error) ||
		error?.code === UNSUPPORTED_ARCHIVED_EMAIL_FILTER_CODE ||
		isArchivedEmailCancellationError(error)
	) {
		throw error;
	}

	throw createUiError(
		ARCHIVED_EMAIL_ERROR_CODES.LIST_FAILED,
		ARCHIVED_EMAIL_ERROR_MESSAGES.list
	);
};

/**
 * Creates the remote stores and page-lifetime detail cache consumed by the
 * Archived Emails page.
 *
 * @param {{api?: object}} options Optional API override for isolated tests.
 * @returns {object} Grid source, header source factory, and detail operations.
 */
export const createArchivedEmailsDataSource = ({
	api = archivedEmailsApi,
} = {}) => {
	const detailCache = new Map();
	const pendingDetails = new Map();

	const listStore = new CustomStore({
		key: "id",
		loadMode: "processed",
		/**
		 * Loads one remote metadata page without prefetching message bodies.
		 *
		 * @param {object} loadOptions DevExtreme grid load options.
		 * @returns {Promise<{data: Array, totalCount: number}>} Grid page.
		 */
		load: async (loadOptions) => {
			try {
				const query =
					mapArchivedEmailLoadOptionsToQuery(loadOptions);
				const result =
					await api.getArchivedEmailsAsync(query);
				return {
					data: result.items,
					totalCount: result.totalCount,
				};
			} catch (error) {
				throwListError(error);
			}
		},
	});

	/**
	 * Creates a bounded remote distinct-value source for one text column.
	 *
	 * @param {string} field Approved header-filter field.
	 * @returns {object} DevExtreme remote filter-value source options.
	 */
	const createHeaderFilterDataSource = (field) => {
		if (!TEXT_FIELD_RULES[field]) throw unsupportedFilter();

		const filterStore = new CustomStore({
			key: "value",
			loadMode: "processed",
			/**
			 * Loads one bounded page of distinct values.
			 *
			 * @param {object} loadOptions Header-filter load options.
			 * @returns {Promise<{data: Array, totalCount: number}>} Values.
			 */
			load: async (loadOptions) => {
				try {
					const query =
						mapArchivedEmailHeaderFilterLoadOptionsToQuery(
							field,
							loadOptions
						);
					const result =
						await api.getArchivedEmailFilterValuesAsync(
							query
						);
					return {
						data: result.items,
						totalCount: result.totalCount,
					};
				} catch (error) {
					throwListError(error);
				}
			},
		});

		// Column header filters accept a Store or DataSourceOptions object.
		// Returning configuration here lets DevExtreme create and manage the
		// popup's DataSource without normalizing an existing instance to empty.
		return {
			store: filterStore,
			paginate: true,
			pageSize: ARCHIVED_EMAIL_DEFAULT_PAGE_SIZE,
			requireTotalCount: true,
		};
	};

	/**
	 * Lazily loads one body and caches only successful results for this page.
	 * Failed promises are removed so a user-requested Retry starts a new call.
	 *
	 * @param {number} id Positive archived-email identifier.
	 * @param {{signal?: AbortSignal}} options Optional unmount signal.
	 * @returns {Promise<{id: number, plainTextBody: string}>} Email detail.
	 */
	const loadDetailAsync = (id, { signal } = {}) => {
		const normalizedId = Number(id);
		if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
			return Promise.reject(
				createUiError(
					ARCHIVED_EMAIL_ERROR_CODES.DETAIL_FAILED,
					ARCHIVED_EMAIL_ERROR_MESSAGES.detail
				)
			);
		}

		if (detailCache.has(normalizedId)) {
			return Promise.resolve(detailCache.get(normalizedId));
		}
		const pending = pendingDetails.get(normalizedId);
		if (pending && !pending.signal?.aborted) {
			return pending.request;
		}

		const request = api
			.getArchivedEmailByIdAsync(normalizedId, { signal })
			.then((detail) => {
				if (!signal?.aborted) detailCache.set(normalizedId, detail);
				return detail;
			})
			.catch((error) => {
				if (
					isArchivedEmailAuthorizationError(error) ||
					isArchivedEmailCancellationError(error)
				) {
					throw error;
				}
				if (
					error?.code ===
					ARCHIVED_EMAIL_ERROR_CODES.NOT_FOUND
				) {
					throw createUiError(
						ARCHIVED_EMAIL_ERROR_CODES.NOT_FOUND,
						ARCHIVED_EMAIL_ERROR_MESSAGES.notFound
					);
				}
				throw createUiError(
					ARCHIVED_EMAIL_ERROR_CODES.DETAIL_FAILED,
					ARCHIVED_EMAIL_ERROR_MESSAGES.detail
				);
			})
			.finally(() => {
				// An aborted request may already have been replaced. Only the
				// request that owns the current entry may remove it.
				if (
					pendingDetails.get(normalizedId)?.request ===
					request
				) {
					pendingDetails.delete(normalizedId);
				}
			});

		pendingDetails.set(normalizedId, { request, signal });
		return request;
	};

	/**
	 * Removes all successful body results when the page is disposed.
	 *
	 * @returns {void}
	 */
	const clearDetailCache = () => {
		detailCache.clear();
	};

	return {
		// Supplying the Store lets DataGrid own its DataSource lifecycle. A
		// shared DataSource cannot be disposed safely during React Strict
		// Mode's intentional effect cleanup and replay.
		dataSource: listStore,
		createHeaderFilterDataSource,
		loadDetailAsync,
		clearDetailCache,
	};
};

export default createArchivedEmailsDataSource;
